import {
    PurchaseReportData,
    PaymentProvider,
    InStorePaymentType,
    PurchaseStatus,
} from "@snackpass/snackpass-types";
import moment from "moment-timezone";
import { t } from "@lingui/core/macro";

import {
    formatPurchaseProvider,
    formatPurchaseChannel,
    nameToFirstNameLastInitial,
} from "#utils/helpers";
import { formatCurrency } from "src/@/lib/format-currency";
import { FULFILLMENT_MAP, Fulfillment } from "#sales-report/types";

import type { BridgeOrderData } from "./hooks/use-bridge-enhanced-orders";

export const withDateQuery = (path: string, search: string): string => {
    const query = new URLSearchParams(
        [...new URLSearchParams(search)].filter(([key]) =>
            ["range", "from", "to"].includes(key),
        ),
    ).toString();
    return query ? `${path}?${query}` : path;
};

export const formatItems = (p: PurchaseReportData): string => {
    const names = p.items.map((item) => {
        const { name } = item;
        let itemText = name;
        if (item.weight) {
            const { amount, unit } = item.weight;
            itemText = `${name}(${amount}${unit})`;
        }
        return itemText;
    });
    return names.join("; ");
};

export const formatPromos = (p: PurchaseReportData): string =>
    [...p.promoNames, ...p.rewardNames].join("; ");

export const formatNotes = (p: PurchaseReportData): string =>
    p.notes.join("; ");

/**
 * Calculates the completion time in minutes between start and complete events
 * @param events Array of events with type and createdAt timestamp
 * @returns Number of minutes between start and complete events, or null if incomplete
 */
export const calculateCompletionTimeInMinutes = (
    events: { type: string; createdAt: string | Date }[] = [],
): number | null => {
    const startEvent = events.find((event) => event.type === "STARTED");
    const completeEvent = events.find((event) => event.type === "COMPLETED");

    if (startEvent?.createdAt && completeEvent?.createdAt) {
        const start = new Date(startEvent.createdAt).getTime();
        const end = new Date(completeEvent.createdAt).getTime();
        return (end - start) / (1000 * 60); // Convert to minutes
    }
    return null;
};

export const formatRefund = (p: PurchaseReportData): string =>
    p.refundedAmount ? formatCurrency(p.refundedAmount) : "";

export const formatStatus = (p: PurchaseReportData): string => {
    switch (p.status) {
        case PurchaseStatus.received:
            return "Received";
        case PurchaseStatus.started:
            return "Started";
        case PurchaseStatus.canceled:
            return p.refundedAmount > 0 ? "Refunded" : "Canceled";
        case PurchaseStatus.completed:
            return "Completed";
        default:
            return p.status ?? "Received";
    }
};

export const formatPaymentMethod = (p: PurchaseReportData): string => {
    switch (p.paymentMethod) {
        case PaymentProvider.inStore:
            switch (p.inStorePaymentType) {
                case InStorePaymentType.other:
                    return "In-Store (Other)";
                case InStorePaymentType.giftCards:
                    return "In-Store (Gift Card)";
                default:
                    return "In-Store (Cash)";
            }
        case PaymentProvider.thirdParty:
            return "Third Party";
        case PaymentProvider.square:
        case PaymentProvider.stripe:
            return "Card";
        case PaymentProvider.giftCards:
            return "Gift Card Credit";
        case undefined:
            return p.hasSpecialGiftAccounting
                ? "Card"
                : p.giftCardCredit
                  ? "Gift Card Credit"
                  : p.storeCredit
                    ? "Store Credit"
                    : p.snackpassCredit
                      ? "Snackpass Credit"
                      : "-";
        default:
            return p.paymentMethod;
    }
};

export const getTwoDigitDateString = (
    timezone: string,
    date: Date | string | null,
): string => {
    if (!date) return "";
    const momentDate = moment(date);
    return timezone
        ? momentDate.tz(timezone).format("h:mm a M/D/YYYY")
        : momentDate.format("h:mm a M/D/YYYY");
};

// Bridge data helper functions

export const getBridgeKitchenStatus = (
    bridge: BridgeOrderData | null,
): string => {
    if (!bridge) return "—";

    const itemsToCheck = bridge.tickets;

    if (!itemsToCheck || itemsToCheck.length === 0) {
        return "No Tickets";
    }

    let latestCompletionTimestamp: string | null = null;

    const allCompleted = itemsToCheck.every((item) => {
        const statusEntries = Object.entries(item.status);
        if (statusEntries.length === 0) return false;

        // Get the latest status (highest timestamp)
        const [latestTimestamp, latestStatus] = statusEntries.sort(
            ([a], [b]) => new Date(b).getTime() - new Date(a).getTime(),
        )[0];

        if (latestStatus === "COMPLETED") {
            if (
                !latestCompletionTimestamp ||
                new Date(latestTimestamp) > new Date(latestCompletionTimestamp)
            ) {
                latestCompletionTimestamp = latestTimestamp;
            }
            return true;
        }
        return false;
    });

    if (allCompleted && latestCompletionTimestamp) {
        return `Completed at ${moment(latestCompletionTimestamp).format("h:mm A")}`;
    }

    return "In Progress";
};

export const getBridgePaymentStatus = (
    bridge: BridgeOrderData | null,
): string => {
    if (!bridge) return "Unpaid";

    // Find the latest payment timestamp once at the beginning
    const latestPaymentTime = findLatestPaymentTimestamp(bridge.payments || []);

    // If check is PAID
    if (bridge.check?.status === "PAID") {
        if (latestPaymentTime) {
            return `Paid at ${moment(latestPaymentTime).format("h:mm A")}`;
        }
        return "PAID";
    }

    // If unpaid but there are payments, show Partially Paid
    if (bridge.payments && bridge.payments.length > 0) {
        return "Partially Paid";
    }

    // Otherwise, unpaid
    return "Unpaid";
};

// Helper function to find the latest successful payment timestamp
const findLatestPaymentTimestamp = (
    payments: Array<{ status: Record<string, string> }>,
): string | null => {
    let latestTimestamp: string | null = null;

    payments.forEach((payment) => {
        const statusEntries = Object.entries(payment.status);
        if (statusEntries.length === 0) return;

        // Get the latest status for this payment (highest timestamp)
        const [latestTime, latestStatus] = statusEntries.sort(
            ([a], [b]) => new Date(b).getTime() - new Date(a).getTime(),
        )[0];

        // Check if this payment has reached a successful final state
        if (
            latestStatus === "SUCCEEDED" ||
            latestStatus === "CAPTURED" ||
            latestStatus === "CONFIRMED"
        ) {
            if (
                !latestTimestamp ||
                new Date(latestTime) > new Date(latestTimestamp)
            ) {
                latestTimestamp = latestTime;
            }
        }
    });

    return latestTimestamp;
};

// Bridge Order Status helper functions
export type StatusEntry = {
    status: string;
    timestamp: string;
    color: string;
    source?: string; // 'ticket' or 'chit' or 'payment'
    sourceId?: string; // ticket/chit/payment identifier
};

export type GroupedStatusEntry = {
    flooredTimestamp: string; // Display timestamp floored to seconds
    exactTimestamp: string; // Exact timestamp for sorting
    statuses: StatusEntry[];
};

export type GroupedTimeline = GroupedStatusEntry[];

const getStatusColor = (status: string): string => {
    const statusColors: Record<string, string> = {
        // Kitchen statuses
        CREATED: "#6b7280",
        SENT: "#3b82f6",
        STARTED: "#f59e0b",
        COMPLETED: "#10b981",
        VOIDED: "#ef4444",

        // Payment statuses
        PENDING: "#6b7280",
        COLLECTED: "#3b82f6",
        CONFIRMED: "#8b5cf6",
        UNCONFIRMED: "#f59e0b",
        CAPTURED: "#10b981",
        SUCCEEDED: "#059669",
        FAILED: "#ef4444",
        CANCELED: "#6b7280",
        REFUNDED: "#f97316",
    };
    return statusColors[status] || "#6b7280";
};

const floorTimestampToSeconds = (timestamp: string): string => {
    const date = new Date(timestamp);
    // Floor to seconds by setting milliseconds to 0
    const flooredDate = new Date(date.getTime());
    flooredDate.setMilliseconds(0);
    return moment(flooredDate).format("h:mm:ss A M/D/YYYY");
};

export const formatTimeWithDateComparison = (
    timestamp: string,
    orderCreatedAt: string,
): { timeText: string; dateText?: string; isDifferentDate: boolean } => {
    const eventDate = new Date(timestamp);
    const orderDate = new Date(orderCreatedAt);

    // Compare dates (ignoring time)
    const eventDateString = eventDate.toDateString();
    const orderDateString = orderDate.toDateString();
    const isDifferentDate = eventDateString !== orderDateString;

    // Format time with leading space for single-digit hours
    const momentTime = moment(eventDate);
    const hour = momentTime.format("h");
    const timeWithoutHour = momentTime.format(":mm:ss A");
    const timeText =
        hour.length === 1
            ? ` ${hour}${timeWithoutHour}`
            : `${hour}${timeWithoutHour}`;

    // Format date if different
    const dateText = isDifferentDate
        ? momentTime.format("M/D/YYYY")
        : undefined;

    return {
        timeText,
        dateText,
        isDifferentDate,
    };
};

const groupStatusesByFlooredTime = (
    allStatuses: StatusEntry[],
): GroupedTimeline => {
    const grouped = new Map<string, GroupedStatusEntry>();

    // Sort by exact timestamp first
    allStatuses.sort(
        (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );

    allStatuses.forEach((status) => {
        const flooredTimestamp = floorTimestampToSeconds(status.timestamp);

        if (!grouped.has(flooredTimestamp)) {
            grouped.set(flooredTimestamp, {
                flooredTimestamp,
                exactTimestamp: status.timestamp,
                statuses: [],
            });
        }

        grouped.get(flooredTimestamp)!.statuses.push(status);
    });

    return Array.from(grouped.values()).sort(
        (a, b) =>
            new Date(a.exactTimestamp).getTime() -
            new Date(b.exactTimestamp).getTime(),
    );
};

export const groupChitsByTicket = (
    chits: Array<{ key: string; status: Record<string, string> }>,
    tickets: Array<{ key: string; status: Record<string, string> }>,
): Map<string, Array<{ key: string; status: Record<string, string> }>> => {
    const chitsByTicket = new Map<
        string,
        Array<{ key: string; status: Record<string, string> }>
    >();

    // Initialize with tickets
    tickets.forEach((ticket) => {
        chitsByTicket.set(ticket.key, []);
    });

    // Group chits by their parent ticket ID
    chits.forEach((chit) => {
        const ticketId = chit.key.split(":")[0];
        if (!chitsByTicket.has(ticketId)) {
            chitsByTicket.set(ticketId, []);
        }
        chitsByTicket.get(ticketId)!.push(chit);
    });

    return chitsByTicket;
};

export type KitchenItem = {
    label: string;
    fullKey: string;
    type: "ticket" | "chit";
    ticketNumber: number;
    chitNumber?: number;
    firstTimestamp: string;
};

export type KitchenEvent = {
    item: KitchenItem;
    flooredTimestamp: string;
    exactTimestamp: string;
    statuses: StatusEntry[];
};

export type KitchenTableData = KitchenEvent[];

export const getKitchenTimeline = (
    tickets: Array<{ key: string; status: Record<string, string> }>,
    chits: Array<{ key: string; status: Record<string, string> }>,
): KitchenTableData => {
    // First, create all kitchen items with proper labeling
    const kitchenItems: KitchenItem[] = [];

    // Sort tickets by creation timestamp
    const sortedTickets = tickets
        .map((ticket) => {
            const createdTimestamp = Object.entries(ticket.status)
                .filter(([_, status]) => status === "CREATED")
                .sort(
                    ([a], [b]) => new Date(a).getTime() - new Date(b).getTime(),
                )[0]?.[0];
            return { ...ticket, createdTimestamp };
        })
        .filter((ticket) => ticket.createdTimestamp)
        .sort(
            (a, b) =>
                new Date(a.createdTimestamp!).getTime() -
                new Date(b.createdTimestamp!).getTime(),
        );

    // Group chits by ticket
    const chitsByTicket = groupChitsByTicket(chits, tickets);

    // Create kitchen items with proper labels
    sortedTickets.forEach((ticket, ticketIndex) => {
        const ticketNumber = ticketIndex + 1;

        // Add ticket item
        kitchenItems.push({
            label: `Ticket ${ticketNumber}`,
            fullKey: ticket.key,
            type: "ticket",
            ticketNumber,
            firstTimestamp: ticket.createdTimestamp!,
        });

        // Add chit items for this ticket
        const ticketChits = chitsByTicket.get(ticket.key) || [];
        const sortedChits = ticketChits
            .map((chit) => {
                const createdTimestamp = Object.entries(chit.status)
                    .filter(([_, status]) => status === "CREATED")
                    .sort(
                        ([a], [b]) =>
                            new Date(a).getTime() - new Date(b).getTime(),
                    )[0]?.[0];
                return { ...chit, createdTimestamp };
            })
            .filter((chit) => chit.createdTimestamp)
            .sort(
                (a, b) =>
                    new Date(a.createdTimestamp!).getTime() -
                    new Date(b.createdTimestamp!).getTime(),
            );

        sortedChits.forEach((chit, chitIndex) => {
            const chitNumber = chitIndex + 1;
            kitchenItems.push({
                label: `Ticket ${ticketNumber} Chit ${chitNumber}`,
                fullKey: chit.key,
                type: "chit",
                ticketNumber,
                chitNumber,
                firstTimestamp: chit.createdTimestamp!,
            });
        });
    });

    // Now collect all events for all items
    const allEvents: KitchenEvent[] = [];

    kitchenItems.forEach((item) => {
        // Get the raw data for this item
        let statusData: Record<string, string> = {};

        if (item.type === "ticket") {
            const ticket = tickets.find((t) => t.key === item.fullKey);
            statusData = ticket?.status || {};
        } else {
            const chit = chits.find((c) => c.key === item.fullKey);
            statusData = chit?.status || {};
        }

        // Group events by floored timestamp
        const eventGroups = new Map<
            string,
            {
                flooredTimestamp: string;
                exactTimestamp: string;
                statuses: StatusEntry[];
            }
        >();

        // Deduplicate statuses - keep only first occurrence of each status type
        const deduplicatedStatuses: Record<string, string> = {};
        Object.entries(statusData)
            .sort(([a], [b]) => a.localeCompare(b)) // Sort by timestamp
            .forEach(([timestamp, status]) => {
                if (!Object.values(deduplicatedStatuses).includes(status)) {
                    deduplicatedStatuses[timestamp] = status;
                }
            });

        Object.entries(deduplicatedStatuses).forEach(([timestamp, status]) => {
            const flooredTimestamp = floorTimestampToSeconds(timestamp);

            if (!eventGroups.has(flooredTimestamp)) {
                eventGroups.set(flooredTimestamp, {
                    flooredTimestamp,
                    exactTimestamp: timestamp,
                    statuses: [],
                });
            }

            const group = eventGroups.get(flooredTimestamp)!;
            group.statuses.push({
                status,
                timestamp,
                color: getStatusColor(status),
            });

            // Keep the earliest exact timestamp for sorting
            if (new Date(timestamp) < new Date(group.exactTimestamp)) {
                group.exactTimestamp = timestamp;
            }
        });

        // Convert groups to events
        eventGroups.forEach((group) => {
            // Sort statuses within the group by exact timestamp
            group.statuses.sort(
                (a, b) =>
                    new Date(a.timestamp).getTime() -
                    new Date(b.timestamp).getTime(),
            );

            allEvents.push({
                item,
                flooredTimestamp: group.flooredTimestamp,
                exactTimestamp: group.exactTimestamp,
                statuses: group.statuses,
            });
        });
    });

    // Sort all events by exact timestamp of first status in each group
    allEvents.sort(
        (a, b) =>
            new Date(a.exactTimestamp).getTime() -
            new Date(b.exactTimestamp).getTime(),
    );

    return allEvents;
};

export const calculateKitchenElapsedTime = (
    tickets: Array<{ key: string; status: Record<string, string> }>,
    chits: Array<{ key: string; status: Record<string, string> }>,
): string => {
    let firstStarted: Date | null = null;
    let lastCompleted: Date | null = null;

    // Check tickets and chits for STARTED and COMPLETED timestamps
    const allItems = [...tickets, ...chits];

    allItems.forEach((item) => {
        // Deduplicate statuses - keep only first occurrence of each status type
        const deduplicatedStatuses: Record<string, string> = {};
        Object.entries(item.status)
            .sort(([a], [b]) => a.localeCompare(b)) // Sort by timestamp
            .forEach(([timestamp, status]) => {
                if (!Object.values(deduplicatedStatuses).includes(status)) {
                    deduplicatedStatuses[timestamp] = status;
                }
            });

        Object.entries(deduplicatedStatuses).forEach(([timestamp, status]) => {
            const date = new Date(timestamp);

            if (status === "STARTED") {
                if (!firstStarted || date < firstStarted) {
                    firstStarted = date;
                }
            }

            if (status === "COMPLETED") {
                if (!lastCompleted || date > lastCompleted) {
                    lastCompleted = date;
                }
            }
        });
    });

    if (!firstStarted || !lastCompleted) {
        return "N/A";
    }

    const elapsedMs =
        (lastCompleted as Date).getTime() - (firstStarted as Date).getTime();
    const elapsedMinutes = Math.floor(elapsedMs / (1000 * 60));

    // Show N/A if over 24 hours (1440 minutes)
    if (elapsedMinutes > 1440) {
        return "N/A";
    }

    if (elapsedMinutes < 60) {
        return `${elapsedMinutes} minutes`;
    }

    const hours = Math.floor(elapsedMinutes / 60);
    const minutes = elapsedMinutes % 60;
    return `${hours}h ${minutes}m`;
};

export const getPaymentTimeline = (
    payments: Array<{ key: string; status: Record<string, string> }>,
): GroupedTimeline => {
    const allStatuses: StatusEntry[] = [];

    // Add payment statuses (no source identifiers for cleaner display)
    payments.forEach((payment) => {
        Object.entries(payment.status).forEach(([timestamp, status]) => {
            allStatuses.push({
                status,
                timestamp,
                color: getStatusColor(status),
            });
        });
    });

    return groupStatusesByFlooredTime(allStatuses);
};

export const calculateOrderTime = (
    orderCreatedAt: string,
    payments: Array<{ key: string; status: Record<string, string> }>,
): string => {
    const orderStart = new Date(orderCreatedAt);
    let collectedTime: Date | null = null;

    // Find the first COLLECTED timestamp
    payments.forEach((payment) => {
        const statusEntries = Object.entries(payment.status);

        statusEntries.forEach(([timestamp, status]) => {
            if (status === "COLLECTED") {
                const date = new Date(timestamp);
                if (!collectedTime || date < collectedTime) {
                    collectedTime = date;
                }
            }
        });
    });

    if (!collectedTime) {
        return "N/A";
    }

    const elapsedMs = (collectedTime as Date).getTime() - orderStart.getTime();
    const elapsedMinutes = Math.floor(elapsedMs / (1000 * 60));

    if (elapsedMinutes < 60) {
        return `${elapsedMinutes} minutes`;
    }

    const hours = Math.floor(elapsedMinutes / 60);
    const minutes = elapsedMinutes % 60;
    return `${hours}h ${minutes}m`;
};

export const getFinalPaymentStatus = (
    payments: Array<{ key: string; status: Record<string, string> }>,
): string | null => {
    if (payments.length === 0) return null;

    let latestTimestamp: string | null = null;
    let latestStatus: string | null = null;

    payments.forEach((payment) => {
        const statusEntries = Object.entries(payment.status);

        statusEntries.forEach(([timestamp, status]) => {
            if (
                !latestTimestamp ||
                new Date(timestamp) > new Date(latestTimestamp)
            ) {
                latestTimestamp = timestamp;
                latestStatus = status;
            }
        });
    });

    return latestStatus;
};

export enum OrderField {
    // Core Order Info
    Details = "details",
    OrderNumber = "orderNumber",
    DateReceived = "dateReceived",
    Status = "status",

    // Bridge-specific status columns
    KitchenStatus = "kitchenStatus",
    PaymentStatus = "paymentStatus",

    // Customer Info
    CustomerName = "customerName",
    CustomerPhone = "customerPhone",
    TableNumber = "tableNumber",
    EmployeeName = "employeeName",

    // Order Details
    Items = "items",
    Promotions = "promotions",

    CompletionTime = "completionTime",
    Notes = "notes",
    ScheduledFor = "scheduledFor",
    Fulfillment = "fulfillment",

    // Order Source
    Channel = "channel",
    Provider = "provider",

    // Base Pricing
    Subtotal = "subtotal",
    CustomSurcharge = "customSurcharge",
    CustomDiscounts = "customDiscounts",
    UpCharge = "upCharge",

    // Delivery & Service Fees
    DeliveryCharge = "deliveryCharge",
    ThirdPartyDeliveryCharge = "thirdPartyDeliveryCharge",
    // FaxFee = "faxFee",
    SnackpassFee = "snackpassFee",
    ProcessingFee = "processingFee",
    ThirdPartyFees = "thirdPartyFees",
    CustomerToStoreFees = "customerToStoreFees",

    // Taxes
    TaxesYouOwe = "taxesYouOwe",
    TaxesRemitted = "taxesRemitted",
    ThirdPartyTaxes = "thirdPartyTaxes",

    // Payment Info
    PaymentMethod = "paymentMethod",
    GiftCardCredit = "giftCardCredit",
    StoreCredit = "storeCredit",
    Tip = "tip",
    Total = "total",
    AmountPaidByCustomer = "amountPaidByCustomer",

    // Sales Calculations
    NetSales = "netSales",
    GrossSales = "grossSales",
    ThirdPartyPayout = "thirdPartyPayout",
    NetPayout = "netPayout",
    ExpectedCash = "expectedCash",

    // Modification Info
    RefundedBy = "refundedBy",
    RefundedAmount = "refundedAmount",
    UpChargedBy = "upChargedBy",
    CashAcceptedBy = "cashAcceptedBy",
    CreatedBy = "createdBy",

    // System IDs (SnackTeam Only)
    ID = "id",
    PayoutID = "payoutId",
    PaidOutOn = "paidOutOn",
    ActualPayout = "actualPayout",
    ReceiptNumber = "receiptNumber",
}

/**
 * Fields included in the Orders CSV, in export order.
 *
 * Keep this as an explicit list rather than deriving it from the table
 * columns: it is the single place to customize the default Orders export.
 */
export const DEFAULT_ORDERS_CSV_COLUMNS: readonly OrderField[] = [
    OrderField.OrderNumber,
    OrderField.DateReceived,
    OrderField.KitchenStatus,
    OrderField.PaymentStatus,
    OrderField.Status,
    OrderField.CustomerName,
    OrderField.CustomerPhone,
    OrderField.Fulfillment,
    OrderField.TableNumber,
    OrderField.Items,
    OrderField.Promotions,
    OrderField.CompletionTime,
    OrderField.Notes,
    OrderField.ScheduledFor,
    OrderField.Channel,
    OrderField.Provider,
    OrderField.Subtotal,
    OrderField.CustomSurcharge,
    OrderField.CustomDiscounts,
    OrderField.UpCharge,
    OrderField.DeliveryCharge,
    OrderField.ThirdPartyDeliveryCharge,
    OrderField.ThirdPartyFees,
    OrderField.CustomerToStoreFees,
    OrderField.TaxesRemitted,
    OrderField.ThirdPartyTaxes,
    OrderField.Total,
    OrderField.AmountPaidByCustomer,
    OrderField.GrossSales,
    OrderField.PaymentMethod,
    OrderField.StoreCredit,
    OrderField.RefundedBy,
    OrderField.RefundedAmount,
    OrderField.UpChargedBy,
    OrderField.CashAcceptedBy,
    OrderField.CreatedBy,
    OrderField.ID,
    OrderField.PayoutID,
    OrderField.PaidOutOn,
    OrderField.ActualPayout,
    OrderField.ReceiptNumber,
    OrderField.EmployeeName,
    OrderField.NetSales,
    OrderField.Tip,
    OrderField.TaxesYouOwe,
    OrderField.SnackpassFee,
    OrderField.ProcessingFee,
    OrderField.ExpectedCash,
    OrderField.ThirdPartyPayout,
    OrderField.GiftCardCredit,
    OrderField.NetPayout,
];

/**
 * Fields included in the CSV downloaded for an individual payout.
 * Adjust this list independently from DEFAULT_ORDERS_CSV_COLUMNS.
 */
export const DEFAULT_PAYOUT_CSV_COLUMNS: readonly OrderField[] = [
    OrderField.OrderNumber,
    OrderField.DateReceived,
    // OrderField.Status,
    // OrderField.CustomerName,
    // OrderField.CustomerPhone,
    //OrderField.Fulfillment,
    // OrderField.TableNumber,
    // OrderField.Items,
    // OrderField.Promotions,
    // OrderField.Notes,
    // OrderField.Channel,
    OrderField.Subtotal,
    OrderField.CustomSurcharge,
    OrderField.CustomDiscounts,
    OrderField.UpCharge,
    // OrderField.DeliveryCharge,
    // OrderField.Total,
    // OrderField.AmountPaidByCustomer,
    // OrderField.StoreCredit,
    // OrderField.RefundedBy,
    // OrderField.RefundedAmount,
    OrderField.NetSales,
    OrderField.Tip,
    OrderField.TaxesYouOwe,
    OrderField.SnackpassFee,
    OrderField.ProcessingFee,
    OrderField.ExpectedCash,
    // OrderField.ThirdPartyPayout,
    OrderField.GiftCardCredit,
    OrderField.NetPayout,
];

/** Fields that can be selected for a custom order-row export. */
export const CUSTOM_EXPORTABLE_ORDER_COLUMNS: readonly OrderField[] =
    Object.values(OrderField).filter(
        (key) =>
            key !== OrderField.Details &&
            key !== OrderField.KitchenStatus &&
            key !== OrderField.PaymentStatus,
    );

type FormatterValue = string | number | Date | null | boolean | unknown;
type FormatterFn = (
    value: FormatterValue,
    data?: PurchaseReportData | null,
    timezone?: string,
) => {
    value: FormatterValue;
    readable: string;
};

type PhoneValue = {
    raw: string;
    formatted: string;
};

export const getBalanceTransactionAmount = (
    data: PurchaseReportData,
): number => {
    if (typeof data.balanceTransactionDetails?.amount === "number") {
        return data.balanceTransactionDetails.amount / 100;
    }

    return 0;
};

export const formatPaidOutDate = (
    date: string | Date | null | undefined,
    timezone?: string,
): string => {
    if (!date) return "";

    return timezone
        ? moment(date).tz(timezone).format("MMM D, YYYY")
        : moment(date).format("MMM D, YYYY");
};

export const getPaidOutDateSortTime = (
    date: string | Date | null | undefined,
    timezone?: string,
): number => {
    if (!date) return Number.NaN;

    const paidOutDate = timezone ? moment(date).tz(timezone) : moment(date);
    return paidOutDate.isValid() ? paidOutDate.valueOf() : Number.NaN;
};

export type PurchaseReportPayoutBreakdown = {
    id: string;
    paidOutOn: string;
    orderCount: number;
    amount: number;
    isPending?: boolean;
};

export const getPurchaseReportPayoutBreakdown = (
    rows: PurchaseReportData[] | undefined,
    timezone?: string,
): PurchaseReportPayoutBreakdown[] => {
    if (!rows?.length) return [];

    const pendingSettlementId = "pending-settlement";
    const payouts = new Map<
        string,
        PurchaseReportPayoutBreakdown & { sortTime: number }
    >();

    rows.forEach((row) => {
        const amount = getBalanceTransactionAmount(row);
        if (amount === 0) return;

        const createdAt = row.payoutDetails?.createdAt;
        const payoutId = row.payoutId;
        if (!createdAt || !payoutId) {
            const pending = payouts.get(pendingSettlementId);
            payouts.set(pendingSettlementId, {
                id: pendingSettlementId,
                paidOutOn: "",
                orderCount: (pending?.orderCount ?? 0) + 1,
                amount: (pending?.amount ?? 0) + amount,
                sortTime: Number.MAX_SAFE_INTEGER,
                isPending: true,
            });
            return;
        }

        const paidOutOn = formatPaidOutDate(createdAt, timezone);
        const sortTime = getPaidOutDateSortTime(createdAt, timezone);
        if (!paidOutOn || !Number.isFinite(sortTime)) return;

        const existing = payouts.get(payoutId);
        payouts.set(payoutId, {
            id: payoutId,
            paidOutOn: existing?.paidOutOn ?? paidOutOn,
            orderCount: (existing?.orderCount ?? 0) + 1,
            amount: (existing?.amount ?? 0) + amount,
            sortTime: existing?.sortTime ?? sortTime,
        });
    });

    return Array.from(payouts.values())
        .sort((a, b) => a.sortTime - b.sortTime)
        .map(({ sortTime: _, ...payout }) => payout);
};

export type ColumnConfig = {
    key: OrderField;
    label: string;
    tooltip?: string;
    accessor: (data: PurchaseReportData) => FormatterValue | PhoneValue;
    formatter: FormatterFn;
    snackTeamOnly?: boolean;
    enableGlobalFilter?: boolean;
    filterFn?: (
        row: { getValue: (columnId: string) => any },
        columnId: string,
        filterValue: string,
    ) => boolean;
};

const moneyFormatter: FormatterFn = (value) => ({
    value,
    readable: formatCurrency(value as number),
});

const dateFormatter: FormatterFn = (value) => ({
    value,
    readable: value ? moment(value).format("h:mm A M/D/YYYY") : "—",
});

const dayInTimezoneFormatter: FormatterFn = (value, _, timezone) => ({
    value,
    readable: value
        ? timezone
            ? moment(value as string | Date)
                  .tz(timezone)
                  .format("YYYY-MM-DD")
            : moment(value as string | Date).format("YYYY-MM-DD")
        : "—",
});

const stringFormatter: FormatterFn = (value) => ({
    value,
    readable: (value ?? "").toString(),
});

export const getOrderColumns = (): Record<OrderField, ColumnConfig> => ({
    [OrderField.Details]: {
        key: OrderField.Details,
        label: t`Details`,
        accessor: (data) => data.purchaseId,
        formatter: () => ({ value: "", readable: t`View` }),
    },
    [OrderField.OrderNumber]: {
        key: OrderField.OrderNumber,
        label: t`Order #`,
        accessor: (data) => data.orderNumber ?? data.receiptNumber,
        formatter: (value) => ({
            value,
            readable: value ? `#${value}` : "—",
        }),
        enableGlobalFilter: true,
    },
    [OrderField.DateReceived]: {
        key: OrderField.DateReceived,
        label: t`Ordered At`,
        accessor: (data) => data.dateReceived,
        formatter: dateFormatter,
    },

    // Bridge-specific columns (these will be handled specially in createOrderColumns)
    [OrderField.KitchenStatus]: {
        key: OrderField.KitchenStatus,
        label: t`Kitchen Status`,
        accessor: (data) => data, // Pass the whole row, will be handled specially
        formatter: () => ({ value: "", readable: "" }), // Will be overridden in cell renderer
    },
    [OrderField.PaymentStatus]: {
        key: OrderField.PaymentStatus,
        label: t`Payment Status`,
        accessor: (data) => data, // Pass the whole row, will be handled specially
        formatter: () => ({ value: "", readable: "" }), // Will be overridden in cell renderer
    },

    [OrderField.Status]: {
        key: OrderField.Status,
        label: t`Status`,
        accessor: (data) => data.status,
        formatter: (_, data) => ({
            value: data?.status ?? "",
            readable: data ? formatStatus(data) : "",
        }),
    },

    // Customer Info
    [OrderField.CustomerName]: {
        key: OrderField.CustomerName,
        label: t`Customer`,
        accessor: (data) => data.customerName,
        formatter: (_, data) => ({
            value: data?.customerName ?? "",
            readable: data?.customerName
                ? nameToFirstNameLastInitial(data?.customerName)
                : "—",
        }),
        enableGlobalFilter: true,
    },
    // Hidden column for searching by customer phone number
    [OrderField.CustomerPhone]: {
        key: OrderField.CustomerPhone,
        label: t`Phone`,
        accessor: (data) => data.customerNumber?.toString() ?? "",
        formatter: stringFormatter,
        enableGlobalFilter: true,
    },
    [OrderField.Fulfillment]: {
        key: OrderField.Fulfillment,
        label: t`Fulfillment`,
        accessor: (data) => data.fulfillment,
        formatter: (_, data) => ({
            value: data?.fulfillment ?? "",
            readable: data
                ? FULFILLMENT_MAP[data.fulfillment as Fulfillment].readable
                : "",
        }),
    },
    [OrderField.TableNumber]: {
        key: OrderField.TableNumber,
        label: t`Table`,
        accessor: (data) => data.tableNumber,
        formatter: stringFormatter,
    },

    // Order Details
    [OrderField.Items]: {
        key: OrderField.Items,
        label: t`Items`,
        accessor: (data) => data.items,
        formatter: (_, data) => ({
            value: "",
            readable: data ? formatItems(data) : "",
        }),
    },
    [OrderField.Promotions]: {
        key: OrderField.Promotions,
        label: t`Promotions`,
        accessor: (data) => [...data.promoNames, ...data.rewardNames],
        formatter: (_, data) => ({
            value: "",
            readable: data ? formatPromos(data) : "",
        }),
    },
    [OrderField.CompletionTime]: {
        key: OrderField.CompletionTime,
        label: t`Completion Time`,
        accessor: (data) => {
            const events = data.purchase?.status || [];
            return calculateCompletionTimeInMinutes(events) !== null
                ? new Date(
                      calculateCompletionTimeInMinutes(events)! * 60 * 1000,
                  ) // Convert minutes back to milliseconds for Date
                : null;
        },
        formatter: (value) => {
            if (!value) return { value: null, readable: "—" };
            const minutes = (value as Date).getTime() / (1000 * 60);
            return {
                value: minutes,
                readable: `${minutes.toFixed(1)} min`,
            };
        },
    },
    [OrderField.Notes]: {
        key: OrderField.Notes,
        label: t`Notes`,
        accessor: (data) => data.notes,
        formatter: (_, data) => ({
            value: "",
            readable: data ? formatNotes(data) : "",
        }),
    },
    [OrderField.ScheduledFor]: {
        key: OrderField.ScheduledFor,
        label: t`Scheduled`,
        accessor: (data) => data.scheduledFor,
        formatter: dateFormatter,
    },

    // Order Source
    [OrderField.Channel]: {
        key: OrderField.Channel,
        label: t`Channel`,
        accessor: (data) => data.source,
        formatter: (_, data) => ({
            value: data?.source ?? "",
            readable: formatPurchaseChannel(
                data?.source,
                data?.purchase?.isPOSMode,
            ),
        }),
    },
    [OrderField.Provider]: {
        key: OrderField.Provider,
        label: t`Provider`,
        accessor: (data) => data.purchase,
        formatter: (_, data) => ({
            value: "",
            readable: formatPurchaseProvider(data?.purchase),
        }),
    },

    // Base Pricing
    [OrderField.Subtotal]: {
        key: OrderField.Subtotal,
        label: t`Subtotal`,
        accessor: (data) => data.subtotal,
        formatter: moneyFormatter,
    },
    [OrderField.CustomSurcharge]: {
        key: OrderField.CustomSurcharge,
        label: t`Custom Surcharge`,
        accessor: (data) => data.customSurchargeTotal || 0,
        formatter: moneyFormatter,
    },
    [OrderField.CustomDiscounts]: {
        key: OrderField.CustomDiscounts,
        label: t`Custom Discounts`,
        accessor: (data) => data.customDiscountsTotal || 0,
        formatter: moneyFormatter,
    },
    [OrderField.UpCharge]: {
        key: OrderField.UpCharge,
        label: t`Up Charge`,
        accessor: (data) => data.upChargeAmount,
        formatter: moneyFormatter,
    },

    // Delivery & Service Fees
    [OrderField.DeliveryCharge]: {
        key: OrderField.DeliveryCharge,
        label: t`Delivery Charge`,
        accessor: (data) => data.delivery1PFee,
        formatter: moneyFormatter,
    },
    [OrderField.ThirdPartyDeliveryCharge]: {
        key: OrderField.ThirdPartyDeliveryCharge,
        label: t`3P Delivery Charge`,
        accessor: (data) => data.delivery3PFee,
        formatter: moneyFormatter,
    },

    [OrderField.SnackpassFee]: {
        key: OrderField.SnackpassFee,
        label: t`Snackpass Fee`,
        accessor: (data) => data.commission,
        formatter: moneyFormatter,
    },
    [OrderField.ProcessingFee]: {
        key: OrderField.ProcessingFee,
        label: t`Processing Fee`,
        accessor: (data) => data.processingFee,
        formatter: moneyFormatter,
    },
    [OrderField.ThirdPartyFees]: {
        key: OrderField.ThirdPartyFees,
        label: t`Estimated Third Party Fees`,
        accessor: (data) => data.thirdPartyFees,
        formatter: moneyFormatter,
    },
    [OrderField.CustomerToStoreFees]: {
        key: OrderField.CustomerToStoreFees,
        label: t`Cust. To Store Fees`,
        accessor: (data) => data.allCustomerToStoreFees,
        formatter: moneyFormatter,
    },

    // Taxes
    [OrderField.TaxesYouOwe]: {
        key: OrderField.TaxesYouOwe,
        label: t`Tax`,
        accessor: (data) => data.storeTaxesToRemit,
        formatter: moneyFormatter,
    },
    [OrderField.TaxesRemitted]: {
        key: OrderField.TaxesRemitted,
        label: t`Taxes Remitted On Your Behalf`,
        accessor: (data) => data.storeTaxesWithheld,
        formatter: moneyFormatter,
        tooltip: t`Taxes that Snackpass will remit to the government on your behalf`,
    },
    [OrderField.ThirdPartyTaxes]: {
        key: OrderField.ThirdPartyTaxes,
        label: t`Estimated Third-Party Taxes`,
        accessor: (data) => data.store3PTaxesToRemit,
        formatter: moneyFormatter,
    },

    [OrderField.Tip]: {
        key: OrderField.Tip,
        label: t`Tips`,
        accessor: (data) => data.tip,
        formatter: moneyFormatter,
    },
    [OrderField.Total]: {
        key: OrderField.Total,
        label: t`Total Collected`,
        accessor: (data) => data.totalSales,
        formatter: moneyFormatter,
    },
    [OrderField.AmountPaidByCustomer]: {
        key: OrderField.AmountPaidByCustomer,
        label: t`Amount Paid by Customer`,
        accessor: (data) => data.totalCustomerPaid,
        formatter: moneyFormatter,
    },
    // Sales Calculations
    [OrderField.NetSales]: {
        key: OrderField.NetSales,
        label: t`Net Sales`,
        accessor: (data) => data.netSales,
        formatter: moneyFormatter,
    },
    [OrderField.GrossSales]: {
        key: OrderField.GrossSales,
        label: t`Gross Sales`,
        accessor: (data) => data.grossSalesAmount,
        formatter: moneyFormatter,
    },
    [OrderField.ThirdPartyPayout]: {
        key: OrderField.ThirdPartyPayout,
        label: t`Estimated Third-Party Payout`,
        accessor: (data) => data.expected3PPayout,
        formatter: moneyFormatter,
    },
    [OrderField.NetPayout]: {
        key: OrderField.NetPayout,
        label: t`Net Payout`,
        accessor: (data) => data.netPayout,
        formatter: moneyFormatter,
    }, // Payment Info
    [OrderField.PaymentMethod]: {
        key: OrderField.PaymentMethod,
        label: t`Payment Method`,
        accessor: (data) => data.paymentMethod,
        formatter: (_, data) => ({
            value: data?.paymentMethod ?? "",
            readable: data ? formatPaymentMethod(data) : "",
        }),
    },
    [OrderField.ExpectedCash]: {
        key: OrderField.ExpectedCash,
        label: t`Cash`,
        accessor: (data) => data.netCashPayments,
        formatter: moneyFormatter,
    },

    [OrderField.GiftCardCredit]: {
        key: OrderField.GiftCardCredit,
        label: t`Gift Card Redemp.`,
        accessor: (data) => data.giftCardCredit,
        formatter: moneyFormatter,
    },
    [OrderField.StoreCredit]: {
        key: OrderField.StoreCredit,
        label: t`Store Credit Redemp.`,
        accessor: (data) => data.storeCredit,
        formatter: moneyFormatter,
    },
    // Modification Info
    [OrderField.RefundedBy]: {
        key: OrderField.RefundedBy,
        label: t`Refunded By`,
        accessor: (data) => data.refundedBy,
        formatter: stringFormatter,
    },
    [OrderField.RefundedAmount]: {
        key: OrderField.RefundedAmount,
        label: t`Refunded Amount`,
        accessor: (data) => data.refundedAmount,
        formatter: moneyFormatter,
    },
    [OrderField.UpChargedBy]: {
        key: OrderField.UpChargedBy,
        label: t`Up-Charged By`,
        accessor: (data) => data.upchargedBy,
        formatter: stringFormatter,
    },
    [OrderField.CashAcceptedBy]: {
        key: OrderField.CashAcceptedBy,
        label: t`Cash Accepted By`,
        accessor: (data) => data.cashAcceptedBy,
        formatter: stringFormatter,
    },
    [OrderField.CreatedBy]: {
        key: OrderField.CreatedBy,
        label: t`Created By`,
        accessor: (data) => data.orderCreatedBy,
        formatter: stringFormatter,
    },

    // System IDs (SnackTeam Only)
    [OrderField.ID]: {
        key: OrderField.ID,
        label: t`ID`,
        accessor: (data) => data.purchaseId,
        formatter: stringFormatter,
        snackTeamOnly: true,
    },
    [OrderField.PayoutID]: {
        key: OrderField.PayoutID,
        label: t`Payout ID`,
        accessor: (data) => data.payoutId,
        formatter: stringFormatter,
        snackTeamOnly: true,
    },
    [OrderField.PaidOutOn]: {
        key: OrderField.PaidOutOn,
        label: t`Paid Out date`,
        accessor: (data) => data.payoutDetails?.createdAt,
        formatter: dayInTimezoneFormatter,
    },
    [OrderField.ActualPayout]: {
        key: OrderField.ActualPayout,
        label: t`Paid Out Amount`,
        accessor: (data) =>
            // Note: should we display "-" instead of zero? Specially for 3p and cash orders
            getBalanceTransactionAmount(data),
        formatter: moneyFormatter,
    },
    [OrderField.ReceiptNumber]: {
        key: OrderField.ReceiptNumber,
        label: t`Legacy Receipt Number`,
        accessor: (data) => data.receiptNumber,
        formatter: stringFormatter,
        snackTeamOnly: true,
    },

    // New column configuration
    [OrderField.EmployeeName]: {
        key: OrderField.EmployeeName,
        label: t`Employee`,
        accessor: (data) => data.purchase?.employeeName,
        formatter: stringFormatter,
        enableGlobalFilter: true,
    },
});

export const formatForCSV = (
    purchases: PurchaseReportData[],
    timezone: string,
    isSnackTeam = false,
    showRemittance = false,
    exportColumns: readonly OrderField[] = DEFAULT_ORDERS_CSV_COLUMNS,
): Record<string, string>[] =>
    purchases.map((purchase) => {
        const allColumns = getOrderColumns();
        const columns = exportColumns
            .map((key) => allColumns[key])
            .filter(
                (col): col is ColumnConfig =>
                    (!col.snackTeamOnly || isSnackTeam) &&
                    (col.key !== OrderField.TaxesRemitted || showRemittance) &&
                    col.key !== OrderField.Details,
            );

        return columns.reduce<Record<string, string>>(
            (acc, col) => ({
                ...acc,
                [col.label]: col.formatter(
                    col.accessor(purchase),
                    purchase,
                    timezone,
                ).readable,
            }),
            {},
        );
    });

// Timeline helper types and functions
export type TimelineTicketGroup = {
    ticket: KitchenItem;
    chits: KitchenItem[];
    events: KitchenEvent[];
    minTime: number;
    maxTime: number;
};

export type TimelineData = {
    groups: TimelineTicketGroup[];
    globalMinTime: number;
    globalMaxTime: number;
    timeRange: number;
};

/**
 * Groups kitchen timeline data by tickets for timeline visualization
 */
export const groupTimelineByTickets = (
    timeline: KitchenTableData,
): TimelineData => {
    if (timeline.length === 0) {
        return {
            groups: [],
            globalMinTime: 0,
            globalMaxTime: 0,
            timeRange: 0,
        };
    }

    // Group by ticket number
    const ticketGroups = new Map<
        number,
        {
            ticket?: KitchenItem;
            chits: KitchenItem[];
            events: KitchenEvent[];
        }
    >();

    // First pass: collect all items and events by ticket
    timeline.forEach((event) => {
        const ticketNumber = event.item.ticketNumber;

        if (!ticketGroups.has(ticketNumber)) {
            ticketGroups.set(ticketNumber, {
                chits: [],
                events: [],
            });
        }

        const group = ticketGroups.get(ticketNumber)!;

        if (event.item.type === "ticket") {
            group.ticket = event.item;
        } else {
            // Only add chit to list if not already present
            if (
                !group.chits.find((chit) => chit.fullKey === event.item.fullKey)
            ) {
                group.chits.push(event.item);
            }
        }

        group.events.push(event);
    });

    // Calculate time ranges and create final groups
    let globalMinTime = Infinity;
    let globalMaxTime = -Infinity;

    const groups: TimelineTicketGroup[] = [];

    // Sort by ticket number
    Array.from(ticketGroups.entries())
        .sort(([a], [b]) => a - b)
        .forEach(([, group]) => {
            if (!group.ticket) return; // Skip if no ticket found

            // Calculate min/max time for this group
            let minTime = Infinity;
            let maxTime = -Infinity;

            group.events.forEach((event) => {
                const timestamp = new Date(event.exactTimestamp).getTime();
                minTime = Math.min(minTime, timestamp);
                maxTime = Math.max(maxTime, timestamp);
            });

            globalMinTime = Math.min(globalMinTime, minTime);
            globalMaxTime = Math.max(globalMaxTime, maxTime);

            // Sort chits by creation time
            group.chits.sort(
                (a, b) =>
                    new Date(a.firstTimestamp).getTime() -
                    new Date(b.firstTimestamp).getTime(),
            );

            groups.push({
                ticket: group.ticket,
                chits: group.chits,
                events: group.events,
                minTime,
                maxTime,
            });
        });

    return {
        groups,
        globalMinTime: globalMinTime === Infinity ? 0 : globalMinTime,
        globalMaxTime: globalMaxTime === -Infinity ? 0 : globalMaxTime,
        timeRange:
            globalMaxTime === -Infinity || globalMinTime === Infinity
                ? 0
                : globalMaxTime - globalMinTime,
    };
};

/**
 * Calculates the position of a timestamp on the timeline as a percentage
 */
export const calculateTimelinePosition = (
    timestamp: string,
    minTime: number,
    timeRange: number,
): number => {
    if (timeRange === 0) return 0;

    const eventTime = new Date(timestamp).getTime();
    const position = ((eventTime - minTime) / timeRange) * 100;
    return Math.max(0, Math.min(100, position));
};

/**
 * Generates time scale markers for the timeline
 */
export const generateTimeScaleMarkers = (
    minTime: number,
    maxTime: number,
    maxMarkers: number = 5,
): Array<{ position: number; label: string; timestamp: number }> => {
    const timeRange = maxTime - minTime;
    if (timeRange === 0) return [];

    const markers: Array<{
        position: number;
        label: string;
        timestamp: number;
    }> = [];

    // Calculate interval based on time range
    const intervalMs = timeRange / (maxMarkers - 1);

    for (let i = 0; i < maxMarkers; i++) {
        const timestamp = minTime + intervalMs * i;
        const position = (i / (maxMarkers - 1)) * 100;
        const date = new Date(timestamp);

        // Format label based on time range
        let label: string;
        if (timeRange < 60 * 60 * 1000) {
            // Less than 1 hour - show seconds
            label = moment(date).format("h:mm:ss A");
        } else if (timeRange < 24 * 60 * 60 * 1000) {
            // Less than 24 hours - show minutes
            label = moment(date).format("h:mm A");
        } else {
            // More than 24 hours - show date
            label = moment(date).format("M/D h:mm A");
        }

        markers.push({
            position,
            label,
            timestamp,
        });
    }

    return markers;
};
